<template>
  <v-page no-padding :white="!isFeedThread">
    <div
      :class="[
        {
          '--comments': isCommentThread,
          '--feed': isFeedThread,
          'bg-white': !isFeedThread,
          'h-full': !isCommentThread
        },
        'message-thread__page'
      ]"
    >
      <template v-if="isLoading || !hasCheckedAuthentication">
        <div
          class="w-100 h-full d-flex align-items-center justify-content-center"
        >
          <v-loader />
        </div>
      </template>
      <template v-else-if="messageThreadId">
        <thread-header :message-thread="messageThread" />

        <message-thread
          :with-reactions="showReactions"
          :message-thread-id="messageThreadId"
          :is-comment-thread="isCommentThread"
          :placeholder="
            isCommentThread
              ? $t('pages.messageThread.show.writeReply')
              : $t('pages.messageThread.show.writeSomething')
          "
        />

        <page-action-panel-modal v-if="!isThread" :page-id="THREAD_HASH" />
      </template>
    </div>

    <thread-participants-modal
      v-if="showParticipantsModal"
      :message-thread-id="messageThreadId"
      @close="handleCloseActions"
    />
    <thread-renaming-modal
      v-if="showRenamingModal"
      :message-thread="messageThread"
      @close="handleCloseActions"
    />
    <alert-dialog
      :title="$t('pages.messageThread.show.dialogConfirmCloseTitle')"
      :actions="[
        {
          title: $t('pages.messageThread.show.dialogConfirmCancel'),
          callback: () => (showLeavingConfirm = false)
        },
        {
          title: $t('pages.messageThread.show.dialogConfirmLeave'),
          callback: handleLeaveGroup
        }
      ]"
      :show="showLeavingConfirm"
      @close="() => (showLeavingConfirm = false)"
    >
      {{ $t("pages.messageThread.show.confirmTitle") }}
    </alert-dialog>
  </v-page>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from "vuex"
import { ACTIONS } from "@/store/page-action-panel"
import { isEmpty } from "@/lib/utils"

import MessageThread from "@/components/message-thread/Thread"
import ThreadHeader from "@/components/message-thread/Header"
import PageActionPanelModal from "@/components/modals/PageActionPanelModal"
import ThreadParticipantsModal from "@/components/modals/ThreadParticipantsModal"
import ThreadRenamingModal from "@/components/modals/ThreadRenamingModal"

const THREAD_HASH = "thread"

export default {
  name: "MessageThreadShow",

  components: {
    MessageThread,
    ThreadHeader,
    PageActionPanelModal,
    ThreadParticipantsModal,
    ThreadRenamingModal,
    AlertDialog: () => import("@/components/AlertDialog")
  },

  data() {
    return {
      isLoading: false,
      showParticipantsModal: false,
      showRenamingModal: false,
      showLeavingConfirm: false,
      THREAD_HASH
    }
  },

  computed: {
    ...mapState("pageActionPanel", ["actions"]),
    ...mapGetters("messages", ["getMessageThread"]),
    ...mapGetters("auth", [
      "isAuthenticated",
      "hasCheckedAuthentication",
      "currentUser"
    ]),

    showReactions() {
      return !!this.$route.query.reactions
    },

    messageThreadId() {
      return this.$route.params.id
    },

    messageThread() {
      return this.getMessageThread(this.messageThreadId)
    },

    isCommentThread() {
      return this.messageThread && !!this.messageThread.parent_message
    },

    isThread() {
      return !!this.messageThread?.parent_message
    },

    isFeedThread() {
      return this.messageThread?.parent_message?.message_thread?.is_feed
    },

    isAdmin() {
      return this.isAuthenticated && this.currentUser?.isAccountAdmin
    }
  },

  beforeDestroy() {
    ;[
      ACTIONS.VIEW_THREAD_PARTICIPANTS,
      ACTIONS.RENAME_THREAD,
      ACTIONS.LEAVE_THREAD
    ].forEach(uid => {
      this.removeAction({
        pageId: THREAD_HASH,
        uid: uid
      })
    })
  },

  watch: {
    hasCheckedAuthentication: {
      immediate: true,
      handler: function() {
        if (
          this.hasCheckedAuthentication &&
          this.isAuthenticated &&
          !this.messageThread
        ) {
          this.isLoading = true
          this.fetchMessageThread(this.messageThreadId)
            .then(() => {
              this.addThreadActions()
            })
            .catch(err => {
              if (err.response.status === 430) {
                this.$router.replace("/")
              }
            })
            .finally(() => {
              this.isLoading = false
            })
        } else if (this.hasCheckedAuthentication && !this.isAuthenticated) {
          this.$router.push("#auth")
        } else {
          this.addThreadActions()
        }
      }
    }
  },

  methods: {
    ...mapActions("messages", [
      "fetchMessageThread",
      "removeParticipantFromThread"
    ]),
    ...mapMutations("pageActionPanel", ["addAction", "removeAction"]),

    addThreadActions() {
      if (this.messageThread && isEmpty(this.actions[THREAD_HASH])) {
        const isOneToOneThread = this.messageThread.is_one_to_one
        const isInThread = this.messageThread.is_in_thread

        // TODO: Consider implementing ordering for page actions
        if (!isOneToOneThread) {
          this.addAction({
            uid: ACTIONS.VIEW_THREAD_PARTICIPANTS,
            pageId: THREAD_HASH,
            icon: "supervised_user_circle",
            text: this.$t("pages.messageThread.show.viewParticipant"),
            callback: this.handleShowParticipants
          })
        }

        if (this.isAdmin) {
          this.addAction({
            uid: ACTIONS.RENAME_THREAD,
            pageId: THREAD_HASH,
            icon: "border_color",
            text: this.$t("pages.messageThread.show.renameGroup"),
            callback: this.handleShowRenaming
          })
        }

        if (isInThread && !isOneToOneThread) {
          this.addAction({
            uid: ACTIONS.LEAVE_THREAD,
            pageId: THREAD_HASH,
            icon: "exit_to_app",
            text: this.$t("pages.messageThread.show.leaveGroup"),
            callback: () => {
              this.showLeavingConfirm = true
            }
          })
        }
      }
    },

    handleShowParticipants() {
      if (!this.isAuthenticated) return

      this.showParticipantsModal = true
      this.$router.push("#thread-participants")
    },

    handleShowRenaming() {
      this.showRenamingModal = true
      this.$router.push("#thread-renaming")
    },

    handleCloseActions(isBack = true) {
      this.showParticipantsModal = false
      this.showRenamingModal = false
      this.$router.removeHash()

      if (isBack) {
        this.$router.push(`#actions-${THREAD_HASH}`)
      }
    },

    handleLeaveGroup() {
      this.removeParticipantFromThread({
        messageThreadId: this.messageThreadId,
        userId: this.currentUser?.id,
        isLeaving: true
      }).then(() => {
        this.$router.replace("/me/messages")
      })
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.message-thread__page {
  background: $color-ui--background;

  &.--feed {
    background: $color-ui--grey-10;
    min-height: 100vh;

    .message-thread {
      margin-top: -1px;
      border: 1px solid $color-ui--grey-20;
      background: $color-ui--white;
      border-bottom-left-radius: var(--border-radius);
      border-bottom-right-radius: var(--border-radius);
    }
  }

  .message-thread {
    height: var(--app-height);
    height: calc(
      var(--app-height) - #{$thread--header-height} - env(safe-area-inset-bottom)
    );
  }

  &.--comments {
    .message-thread {
      height: auto;
      min-height: auto;
    }

    .message-thread__content-wrapper {
      overflow: initial;
    }
  }
}
</style>
